import { useEffect, useRef, useState, type ReactElement } from 'react';
import * as tonkette from '../tonkette';
import { useEditor } from '../store';
import { audibleClips } from '../playback';
import { spitzeBei, welle } from '../wellenvorrat';
import { amSchreibtisch } from './Sheet';

/**
 * **Der Pegelmesser** (Sascha, 28. Oktober, mit dem Bild eines L/R-Messers
 * von 0 bis -54 dB: „bitte sowas bauen“; auf die Frage nach dem Platz:
 * „Neben der Vorschau“).
 *
 * Zwei Balken, links und rechts, gemessen am Abgriff der Tonkette -- also an
 * dem, was wirklich gespielt wird (`tonkette.pegel`) -- und, für alles, was an
 * ihr vorbeigeht, gelesen aus der Wellenform (`nebenDerKette`). Am grossen Schirm ist er
 * von selbst da, am Telefon schaltet man ihn unter „Werkzeuge“ ein.
 *
 * Wie ein Messer sich bewegt, ist Absicht: hoch sofort, runter mit 24 dB je
 * Sekunde (sonst flackert er und man liest nichts), die Spitze bleibt
 * anderthalb Sekunden stehen, und wer 0 dB erreicht, bekommt oben ein rotes
 * Licht, das bleibt, bis man es antippt.
 *
 * Gezeichnet wird ohne React: sechzig Mal die Sekunde einen Balken neu zu
 * rendern hiesse sechzig Mal den Schnittplatz zu fragen, ob sich etwas
 * geändert hat. Die Balken werden direkt gesetzt.
 */

/**
 * **Was an der Tonkette vorbeigeht** (Sascha, 30. Oktober: „bei dem Video
 * ‚kostenloses Buch‘ schlägt der Pegelmesser nicht aus“). Durch die Kette
 * laufen nur Clips auf Tonspuren. Der Ton eines Videoclips kommt aus seinem
 * Videoelement, und liegt ein fertiger Vorschau-Film vor, kommt aller Ton
 * aus ihm -- beides hörte der Messer nicht. Für diese Clips liest er die
 * Wellenform an der Stelle des Kopfes, mit derselben Lautstärke, denselben
 * Blenden und Stummschaltungen wie die Vorschau (`audibleClips`). Das ist
 * Mono -- links und rechts gleich --, die Kette misst weiter in Stereo.
 */
function nebenDerKette(): number {
  const s = useEditor.getState();
  if (!s.playing) return 0;
  const inDerKette = new Set(tonkette.klingt());
  const assets = new Map(s.assets.map((a) => [a.id, a]));
  let summe = 0;
  for (const a of audibleClips(s.timeline, s.playhead, (id) => assets.get(id)?.hasAudio ?? false)) {
    if (inDerKette.has(a.clip.id) || a.clip.source.kind !== 'asset') continue;
    const asset = assets.get(a.clip.source.assetId);
    if (!asset?.hasAudio || !asset.waveformUrl) continue;
    const peaks = welle(asset.waveformUrl);
    if (peaks) summe += spitzeBei(peaks, asset.durationSec, a.mediaTime) * a.volume;
  }
  return summe;
}

/** Wie tief die Skala reicht. Darunter ist es still. */
const UNTEN = -60;
const STRICHE = [0, -6, -12, -18, -24, -30, -36, -42, -48, -54];
/** Wie schnell der Balken fällt, in dB je Sekunde. */
const FALL = 24;
/** Wie lange die Spitze stehen bleibt. */
const HALTEN_MS = 1500;
/** Ab hier gilt ein Kanal als übersteuert (0 dBFS). */
const VOLL = 0.999;

function anteil(db: number): number {
  return Math.max(0, Math.min(1, (db - UNTEN) / -UNTEN));
}

function dezibel(spitze: number): number {
  return spitze > 0 ? 20 * Math.log10(spitze) : -Infinity;
}

/** Am grossen Schirm? Und das auch nach dem Drehen oder Verkleinern. */
function useBreit(): boolean {
  const [breit, setBreit] = useState(amSchreibtisch);
  useEffect(() => {
    const mq = window.matchMedia?.('(min-width: 1024px)');
    if (!mq) return;
    const neu = () => setBreit(mq.matches);
    mq.addEventListener('change', neu);
    return () => mq.removeEventListener('change', neu);
  }, []);
  return breit;
}

/** Ob der Messer gerade zu sehen ist -- für die Bühne, die ihm Platz macht. */
export function usePegelmesserAn(): boolean {
  const wahl = useEditor((s) => s.pegelWahl);
  const breit = useBreit();
  return (wahl ?? (breit ? 'an' : 'aus')) === 'an';
}

export function Pegelmesser(): ReactElement | null {
  const an = usePegelmesserAn();
  const wurzel = useRef<HTMLDivElement>(null);
  const deckel = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)] as const;
  const spitzen = [useRef<HTMLDivElement>(null), useRef<HTMLDivElement>(null)] as const;
  const [ueber, setUeber] = useState<[boolean, boolean]>([false, false]);
  const ueberRef = useRef<[boolean, boolean]>([false, false]);

  // Die Balken: ein Takt je Bild, solange der Messer zu sehen ist.
  useEffect(() => {
    if (!an) return;
    const stand = [UNTEN, UNTEN];
    const spitze = [UNTEN, UNTEN];
    const seit = [0, 0];
    let zuletzt = performance.now();
    let raf = 0;
    const takt = (jetzt: number) => {
      const dt = Math.min(0.25, (jetzt - zuletzt) / 1000);
      zuletzt = jetzt;
      const kette = tonkette.pegel();
      const daneben = nebenDerKette();
      const roh = [(kette?.[0] ?? 0) + daneben, (kette?.[1] ?? 0) + daneben] as const;
      for (const k of [0, 1] as const) {
        const db = Math.max(UNTEN, dezibel(roh[k]));
        stand[k] = db >= stand[k]! ? db : Math.max(UNTEN, stand[k]! - FALL * dt);
        if (db >= spitze[k]!) {
          spitze[k] = db;
          seit[k] = jetzt;
        } else if (jetzt - seit[k]! > HALTEN_MS) {
          spitze[k] = Math.max(UNTEN, spitze[k]! - FALL * dt);
        }
        const d = deckel[k].current;
        if (d) d.style.transform = `scaleY(${1 - anteil(stand[k]!)})`;
        const s = spitzen[k].current;
        if (s) {
          s.style.bottom = `${anteil(spitze[k]!) * 100}%`;
          s.style.opacity = spitze[k]! > UNTEN ? '1' : '0';
        }
        if (roh[k] >= VOLL && !ueberRef.current[k]) {
          ueberRef.current = k === 0 ? [true, ueberRef.current[1]] : [ueberRef.current[0], true];
          setUeber(ueberRef.current);
        }
      }
      // Für den Durchgang und die Diagnose lesbar, ohne zu raten.
      const w = wurzel.current;
      if (w) {
        w.dataset.pegelL = stand[0]!.toFixed(1);
        w.dataset.pegelR = stand[1]!.toFixed(1);
        w.dataset.spitzeL = spitze[0]!.toFixed(1);
        w.dataset.spitzeR = spitze[1]!.toFixed(1);
        // Was davon aus der Wellenform kam -- für den Durchgang.
        w.dataset.daneben = Math.max(UNTEN, dezibel(daneben)).toFixed(1);
      }
      raf = requestAnimationFrame(takt);
    };
    raf = requestAnimationFrame(takt);
    return () => cancelAnimationFrame(raf);
    // Die Refs sind fest; neu angefangen wird nur, wenn der Messer kommt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [an]);

  // **Nicht unter die Transportleiste.** Gemessen, nicht geschätzt -- wie die
  // Bühne selbst (Preview.tsx): am Telefon liegt die Leiste über die ganze
  // Breite, am grossen Schirm oft daneben.
  useEffect(() => {
    if (!an) return;
    const w = wurzel.current;
    const buehne = w?.parentElement;
    if (!w || !buehne) return;
    const legen = () => {
      const box = buehne.getBoundingClientRect();
      const ich = w.getBoundingClientRect();
      let oben = 10;
      let unten = 10;
      for (const leiste of buehne.querySelectorAll<HTMLElement>('.transport')) {
        const bar = leiste.getBoundingClientRect();
        if (bar.height === 0 || bar.right <= ich.left || bar.left >= ich.right) continue;
        if (bar.top - box.top < box.bottom - bar.bottom) oben = Math.max(oben, bar.bottom - box.top + 8);
        else unten = Math.max(unten, box.bottom - bar.top + 8);
      }
      w.style.top = `${oben}px`;
      w.style.bottom = `${unten}px`;
    };
    legen();
    const ro = new ResizeObserver(legen);
    ro.observe(buehne);
    for (const leiste of buehne.querySelectorAll<HTMLElement>('.transport')) ro.observe(leiste);
    return () => ro.disconnect();
  }, [an]);

  if (!an) return null;
  return (
    <div className="pegelmesser" ref={wurzel} data-pegelmesser data-ueber={ueber.map((u) => (u ? '1' : '0')).join('')} aria-label="Pegel links und rechts">
      <div className="pegel-lichter">
        {ueber.map((u, k) => (
          <button
            key={k}
            className={`pegel-licht${u ? ' an' : ''}`}
            title={u ? 'Übersteuert -- tippen zum Zurücksetzen' : 'Übersteuert noch nicht'}
            aria-label={k === 0 ? 'Übersteuert links' : 'Übersteuert rechts'}
            onClick={() => {
              ueberRef.current = [false, false];
              setUeber([false, false]);
            }}
          />
        ))}
      </div>
      <div className="pegel-feld">
        {[0, 1].map((k) => (
          <div key={k} className="pegel-kanal" data-kanal={k === 0 ? 'L' : 'R'}>
            <div className="pegel-deckel" ref={deckel[k as 0 | 1]} />
            <div className="pegel-spitze" ref={spitzen[k as 0 | 1]} />
          </div>
        ))}
        <div className="pegel-skala" aria-hidden="true">
          {STRICHE.map((db) => (
            <span key={db} style={{ top: `${(1 - anteil(db)) * 100}%` }}>
              {db}
            </span>
          ))}
        </div>
      </div>
      <div className="pegel-fuss" aria-hidden="true">
        <span>L</span>
        <span>R</span>
        <span>dB</span>
      </div>
    </div>
  );
}
